import React, { useState, useEffect, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import ViewerNavbar from "./ViewerNavbar";
import API from "../../services/api.ts";
import { getMediaUrl } from "../../services/media.ts";
import "./DV2Viewer.css";
import { resolveAllowedViewerUrl } from '../../utils/safeUrl';

const DV2Viewer = () => {
    // Get parameters from URL
    const urlParams = new URLSearchParams(window.location.search);
    const studyIdParam = urlParams.get('study_id');
    const patientIdParam = urlParams.get('patient_id');
    // New format: "id:source_model,id:source_model" or legacy format: "id,id"
    const dicomDataParam = urlParams.get('dicom_data');
    const dicomIdsParam = urlParams.get('dicom_ids'); // Legacy: DICOM image IDs for OHIF
    const imageDataParam = urlParams.get('image_data');
    const imageIdsParam = urlParams.get('image_ids'); // Legacy: Regular image IDs (JPG/PNG)
    const documentDataParam = urlParams.get('document_data');
    const documentIdParam = urlParams.get('document_id'); // Legacy: single document
    const viewerUrlParam = urlParams.get('viewer_url'); // Pre-computed OHIF URL

    const [studyId] = useState(studyIdParam || localStorage.getItem('studyId'));
    const [patientId] = useState(patientIdParam || 'Unknown');
    const [studyName, setStudyName] = useState("");
    const [labels, setLabels] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [scores, setScores] = useState([]);
    const [shareAnnotations, setShareAnnotations] = useState(false);

    // OHIF viewer URL state
    // Allowlisted at the point of first use -- viewerUrlParam is attacker-controllable.
    const [ohifUrl, setOhifUrl] = useState(
        viewerUrlParam ? resolveAllowedViewerUrl(decodeURIComponent(viewerUrlParam)) : null
    );
    const [ohifLoading, setOhifLoading] = useState(!viewerUrlParam && (dicomDataParam || dicomIdsParam));
    const [studyInstanceUID, setStudyInstanceUID] = useState(null);

    // Image state
    const [imagesData, setImagesData] = useState([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // Document state
    const [documentData, setDocumentData] = useState(null);
    const [documentContent, setDocumentContent] = useState("");

    const [loading, setLoading] = useState(true);

    // Fetch study info including labels, questions, and scores
    const fetchStudyInfo = useCallback(async () => {
        if (!studyId) return;

        try {
            const response = await API.get(`/studies/${studyId}/`);
            setStudyName(response.data.name);
            setLabels(response.data.labels || []);
            setQuestions(response.data.questions || []);
            setScores(response.data.scores || []);
            setShareAnnotations(response.data.share_annotations || false);
        } catch (err) {
            console.error("Error fetching study info:", err);
        }
    }, [studyId]);

    // Launch OHIF viewer for DICOM files
    const launchOhifViewer = useCallback(async () => {
        const hasDicomData = dicomDataParam || dicomIdsParam;
        if (!hasDicomData || viewerUrlParam) return;

        setOhifLoading(true);
        try {
            let dataIds = [];

            if (dicomDataParam) {
                // New format: "id:source_model,id:source_model"
                dataIds = dicomDataParam.split(',').map(item => {
                    const [id] = item.split(':');
                    return parseInt(id.trim());
                }).filter(id => !isNaN(id));
            } else if (dicomIdsParam) {
                // Legacy format: just IDs
                dataIds = dicomIdsParam.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
            }

            if (dataIds.length === 0) {
                setOhifLoading(false);
                return;
            }

            const response = await API.post('/viewers/launch_viewer/', {
                viewer_name: 'ohif',
                data_ids: dataIds
            });

            if (response.data.viewer_url) {
                setOhifUrl(resolveAllowedViewerUrl(response.data.viewer_url));
            }
        } catch (err) {
            console.error("Error launching OHIF viewer:", err);
        } finally {
            setOhifLoading(false);
        }
    }, [dicomDataParam, dicomIdsParam, viewerUrlParam]);

    // Fetch regular images data (JPG/PNG)
    const fetchImagesData = useCallback(async () => {
        const hasImageData = imageDataParam || imageIdsParam;
        if (!hasImageData) return;

        // Parse image items - support new format with source model
        let imageItems = [];

        if (imageDataParam) {
            // New format: "id:source_model,id:source_model"
            imageItems = imageDataParam.split(',').map(item => {
                const [id, source] = item.split(':');
                return { id: id.trim(), source: source?.trim() || 'MedicalImage' };
            }).filter(item => item.id);
        } else if (imageIdsParam) {
            // Legacy format: just IDs, assume MedicalImage
            imageItems = imageIdsParam.split(',').map(id => ({
                id: id.trim(),
                source: 'MedicalImage'
            })).filter(item => item.id);
        }

        if (imageItems.length === 0) return;

        try {
            const imagesArray = [];

            for (const imageItem of imageItems) {
                try {
                    if (imageItem.source === 'PatientData') {
                        // Fetch from PatientData endpoint
                        const response = await API.get(`/data_manager/patient-data/${imageItem.id}/`);
                        const itemData = response.data;
                        imagesArray.push({
                            ...itemData,
                            filePath: itemData.file || itemData.location,
                            fileName: itemData.filename || 'Image'
                        });
                    } else {
                        // Fetch from MedicalImage endpoint
                        const imageResponse = await API.get(`/data_manager/medical-images/${imageItem.id}/`);
                        const imgData = imageResponse.data;

                        const filesResponse = await API.get(`/data_manager/medical-images/${imageItem.id}/files/`);
                        const filesData = filesResponse.data;

                        if (filesData.files && filesData.files.length > 0) {
                            imagesArray.push({
                                ...imgData,
                                filePath: filesData.files[0].path,
                                fileName: filesData.files[0].name
                            });
                        } else {
                            imagesArray.push({
                                ...imgData,
                                filePath: imgData.image,
                                fileName: imgData.image ? imgData.image.split('/').pop() : 'Unknown'
                            });
                        }
                    }
                } catch (err) {
                    console.error(`Error fetching image ${imageItem.id}:`, err);
                }
            }

            setImagesData(imagesArray);
        } catch (err) {
            console.error("Error fetching images:", err);
        }
    }, [imageDataParam, imageIdsParam]);

    // Fetch document data
    const fetchDocumentData = useCallback(async () => {
        const hasDocumentData = documentDataParam || documentIdParam;
        if (!hasDocumentData) return;

        // Parse document items - support new format with source model
        let docItem = null;

        if (documentDataParam) {
            // New format: "id:source_model,id:source_model" - take first one
            const items = documentDataParam.split(',').map(item => {
                const [id, source] = item.split(':');
                return { id: id.trim(), source: source?.trim() || 'MedicalDocument' };
            }).filter(item => item.id);
            docItem = items[0];
        } else if (documentIdParam) {
            // Legacy format: just ID
            docItem = { id: documentIdParam, source: 'MedicalDocument' };
        }

        if (!docItem) return;

        try {
            let docData;

            if (docItem.source === 'PatientData') {
                // Fetch from PatientData endpoint
                const response = await API.get(`/data_manager/patient-data/${docItem.id}/`);
                docData = response.data;
                // Map PatientData fields to document-like structure
                docData.document_type = docData.original_file_type || 'txt';
                docData.filename = docData.filename || 'Document';
                docData.file = docData.file || docData.location;
            } else {
                // Fetch from MedicalDocument endpoint
                const docResponse = await API.get(`/data_manager/medical-documents/${docItem.id}/`);
                docData = docResponse.data;
            }

            setDocumentData(docData);

            const textTypes = ['txt', 'md', 'text', 'markdown'];
            if (textTypes.includes(docData.document_type?.toLowerCase())) {
                try {
                    let contentResponse;
                    if (docItem.source === 'PatientData') {
                        contentResponse = await API.get(`/data_manager/patient-data/${docItem.id}/content/`);
                    } else {
                        contentResponse = await API.get(`/data_manager/medical-documents/${docItem.id}/content/`);
                    }
                    if (contentResponse.data && contentResponse.data.content) {
                        setDocumentContent(contentResponse.data.content);
                    }
                } catch (err) {
                    console.error("Error fetching document content:", err);
                    setDocumentContent("Unable to load document content");
                }
            }
        } catch (err) {
            console.error("Error fetching document:", err);
        }
    }, [documentDataParam, documentIdParam]);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([
                fetchStudyInfo(),
                launchOhifViewer(),
                fetchImagesData(),
                fetchDocumentData()
            ]);
            setLoading(false);
        };
        loadData();
    }, [fetchStudyInfo, launchOhifViewer, fetchImagesData, fetchDocumentData]);

    // Extract StudyInstanceUID from OHIF URL
    useEffect(() => {
        if (ohifUrl) {
            const studyUid = ohifUrl.match(/StudyInstanceUIDs=([^&]+)/)?.[1];
            if (studyUid) {
                setStudyInstanceUID(decodeURIComponent(studyUid));
            }
        }
    }, [ohifUrl]);

    const getImageUrl = (filePath) => {
        if (!filePath) return null;
        return getMediaUrl(filePath);
    };

    const getDocumentIcon = (docType) => {
        const type = docType?.toLowerCase();
        switch (type) {
            case 'pdf': return 'fa-file-pdf-o';
            case 'doc':
            case 'docx': return 'fa-file-word-o';
            case 'txt':
            case 'md':
            case 'text':
            case 'markdown': return 'fa-file-text-o';
            default: return 'fa-file-o';
        }
    };

    // Helper to check if document type is text-based (displayable inline)
    const isTextDocument = (docType) => {
        const textTypes = ['txt', 'md', 'text', 'markdown'];
        return textTypes.includes(docType?.toLowerCase());
    };

    const handleDownloadDocument = () => {
        if (documentData?.file) {
            const fileUrl = getMediaUrl(documentData.file);
            window.open(fileUrl, '_blank');
        }
    };

    const handlePrevImage = () => {
        setCurrentImageIndex(prev => (prev > 0 ? prev - 1 : imagesData.length - 1));
    };

    const handleNextImage = () => {
        setCurrentImageIndex(prev => (prev < imagesData.length - 1 ? prev + 1 : 0));
    };

    const currentImage = imagesData[currentImageIndex] || null;

    // Build available data types and series for annotation level filtering
    const getAvailableDataTypes = () => {
        const types = [];
        if (ohifUrl) types.push('imaging');
        if (imagesData.length > 0) types.push('image');
        if (documentData) types.push('document');
        return types;
    };

    const getAvailableSeries = () => {
        const series = [];
        const seenIds = new Set();

        // Add DICOM series
        if (ohifUrl) {
            const studyUid = ohifUrl.match(/StudyInstanceUIDs=([^&]+)/)?.[1];
            const id = studyUid || 'dicom-study';
            if (!seenIds.has(id)) {
                seenIds.add(id);
                series.push({
                    id,
                    name: studyUid ? `DICOM Study: ${studyUid.slice(0, 20)}...` : 'DICOM Study',
                    dataType: 'imaging'
                });
            }
        }

        // Add image series
        imagesData.forEach((image) => {
            const id = `image-${image.id}`;
            if (!seenIds.has(id)) {
                seenIds.add(id);
                series.push({
                    id,
                    name: image.fileName || image.filename || `Image ${image.id}`,
                    dataType: 'image'
                });
            }
        });

        // Add document series
        if (documentData) {
            const id = `document-${documentData.id}`;
            if (!seenIds.has(id)) {
                seenIds.add(id);
                series.push({
                    id,
                    name: documentData.filename || documentData.title || `Document ${documentData.id}`,
                    dataType: 'document'
                });
            }
        }

        return series;
    };

    return (
        <div className="dv2-viewer-page">
            <ViewerNavbar
                studyId={studyId}
                studyName={studyName}
                labels={labels}
                questions={questions}
                scores={scores}
                patientId={patientId}
                shareAnnotations={shareAnnotations}
                studyInstanceUID={studyInstanceUID}
                availableDataTypes={getAvailableDataTypes()}
                availableSeries={getAvailableSeries()}
            />

            <div className="dv2-viewer-content">
                {loading ? (
                    <div className="dv2-viewer-loading">
                        <div className="spinner"></div>
                        <p>Loading patient data...</p>
                    </div>
                ) : (
                    <>
                        {/* Patient Header */}
                        <div className="dv2-patient-header">
                            <div className="patient-info">
                                <i className="fa fa-user-circle"></i>
                                <span className="patient-label">Patient:</span>
                                <span className="patient-id-value">{patientId}</span>
                            </div>
                        </div>

                        {/* Main Split Layout */}
                        <div className="dv2-main-container">
                            {/* Left Panel - OHIF DICOM Viewer */}
                            <div className="dv2-left-panel">
                                <div className="dv2-panel-header">
                                    <i className="fa fa-heartbeat"></i>
                                    <span>DICOM Viewer</span>
                                </div>
                                <div className="dv2-panel-content dicom-content">
                                    {ohifLoading ? (
                                        <div className="dv2-loading-state">
                                            <div className="spinner"></div>
                                            <p>Loading DICOM viewer...</p>
                                        </div>
                                    ) : ohifUrl ? (
                                        <iframe
                                            src={ohifUrl}
                                            title="OHIF DICOM Viewer"
                                            className="dv2-ohif-iframe"
                                            referrerPolicy="no-referrer"
                                            allow="fullscreen"
                                        />
                                    ) : (
                                        <div className="dv2-no-content">
                                            <i className="fa fa-medkit"></i>
                                            <p>No DICOM data available</p>
                                            <small>Select DICOM files from the study dashboard</small>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Vertical Divider */}
                            <div className="dv2-vertical-divider">
                                <div className="divider-line"></div>
                            </div>

                            {/* Right Panel - Document (top) + Image (bottom) */}
                            <div className="dv2-right-panel">
                                {/* Top Right - Document */}
                                <div className="dv2-right-top">
                                    <div className="dv2-panel-header">
                                        <i className={`fa ${documentData ? getDocumentIcon(documentData.document_type) : 'fa-file-text-o'}`}></i>
                                        <span>Document</span>
                                        {documentData?.filename && (
                                            <span className="panel-filename">{documentData.filename}</span>
                                        )}
                                        {documentData && !isTextDocument(documentData.document_type) && (
                                            <button
                                                className="download-btn"
                                                onClick={handleDownloadDocument}
                                                title="Download document"
                                            >
                                                <i className="fa fa-download"></i>
                                            </button>
                                        )}
                                    </div>
                                    <div className="dv2-panel-content document-content">
                                        {documentData ? (
                                            isTextDocument(documentData.document_type) && documentContent ? (
                                                <div className="text-document markdown-content">
                                                    <ReactMarkdown>{documentContent}</ReactMarkdown>
                                                </div>
                                            ) : documentData.document_type?.toLowerCase() === 'pdf' ? (
                                                <div className="pdf-document">
                                                    <iframe
                                                        src={getMediaUrl(documentData.file)}
                                                        title="PDF Document"
                                                        className="pdf-iframe"
                                                    />
                                                </div>
                                            ) : (
                                                <div className="other-document">
                                                    <i className={`fa ${getDocumentIcon(documentData.document_type)}`}></i>
                                                    <p>{documentData.filename}</p>
                                                    <span className="doc-type-badge">
                                                        {documentData.document_type?.toUpperCase()}
                                                    </span>
                                                    <button className="open-btn" onClick={handleDownloadDocument}>
                                                        <i className="fa fa-external-link"></i>
                                                        Open Document
                                                    </button>
                                                </div>
                                            )
                                        ) : (
                                            <div className="dv2-no-content">
                                                <i className="fa fa-file-text-o"></i>
                                                <p>No document available</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Horizontal Divider */}
                                <div className="dv2-horizontal-divider">
                                    <div className="divider-line-h"></div>
                                </div>

                                {/* Bottom Right - Image */}
                                <div className="dv2-right-bottom">
                                    <div className="dv2-panel-header">
                                        <i className="fa fa-image"></i>
                                        <span>Image{imagesData.length > 1 ? 's' : ''}</span>
                                        {imagesData.length > 1 && (
                                            <span className="image-counter">
                                                {currentImageIndex + 1} / {imagesData.length}
                                            </span>
                                        )}
                                        {currentImage?.fileName && (
                                            <span className="panel-filename">{currentImage.fileName}</span>
                                        )}
                                    </div>
                                    <div className="dv2-panel-content image-content">
                                        {imagesData.length > 0 ? (
                                            <div className="image-viewer-wrapper">
                                                {imagesData.length > 1 && (
                                                    <button
                                                        className="image-nav-btn prev-btn"
                                                        onClick={handlePrevImage}
                                                        title="Previous image"
                                                    >
                                                        <i className="fa fa-chevron-left"></i>
                                                    </button>
                                                )}
                                                {currentImage?.filePath ? (
                                                    <img
                                                        src={getImageUrl(currentImage.filePath)}
                                                        alt={currentImage.description || currentImage.fileName || 'Medical Image'}
                                                        className="viewer-image"
                                                    />
                                                ) : (
                                                    <div className="dv2-no-content">
                                                        <i className="fa fa-file-image-o"></i>
                                                        <p>Image not found</p>
                                                    </div>
                                                )}
                                                {imagesData.length > 1 && (
                                                    <button
                                                        className="image-nav-btn next-btn"
                                                        onClick={handleNextImage}
                                                        title="Next image"
                                                    >
                                                        <i className="fa fa-chevron-right"></i>
                                                    </button>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="dv2-no-content">
                                                <i className="fa fa-image"></i>
                                                <p>No image available</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default DV2Viewer;
